import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { IonPage, useIonViewDidEnter } from '@ionic/react';
import { useIsKeyBoardShown } from 'src/hooks/useIsKeyboardShown';
import {
  Container_v2 as Container,
  AppHeader_v2 as AppHeader,
  Button_v2 as Button,
  AppFooter_v2 as AppFooter,
  ScreenInput,
  Typography,
  TypographyVariantsEnum,
  IconArrowLeft,
} from '@mindly/ui-components';
import useAgoraRtm from 'src/context/AgoraContext/AgoraContext';
import useToast from 'src/context/ToastContext/ToastContext';
import { useUpdateProfile } from 'src/shared/hooks/useUpdateProfile';
import { userSelector } from 'src/redux/user/selector';
import { usersPsychologistsSelector } from 'src/redux/usersPsychologists/selector';
import { setUser } from 'src/redux/user/reducer';
import { updateUserPersonalData } from 'src/api/user';
import { MessageTypes, UpdateNameMessage } from 'src/pages/types';

const ProfileName: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation('profile');
  const { t: appT } = useTranslation('app');
  const { showToast } = useToast();
  const { successfullyUpdatedProfile } = useUpdateProfile();
  const isKeyboardShown = useIsKeyBoardShown();
  const user = useSelector(userSelector);
  const usersPsychologistList = useSelector(usersPsychologistsSelector);
  const { isLoggedIn, loginAgoraClient, agoraClient } = useAgoraRtm();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState((user?.display_name as string) || (user?.displayName as string));

  useIonViewDidEnter(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  const onSubmitAction = async () => {
    if (inputValue) {
      setIsLoading(true);
      const { data, error, message } = await updateUserPersonalData({ display_name: inputValue });

      setIsLoading(false);
      if (error) {
        console.warn('Error updating user name', error);
        showToast({ message: message ? appT(message) : 'Something went wrong' });
        return;
      }

      if (usersPsychologistList) {
        if (!isLoggedIn) {
          await loginAgoraClient();
        }
        const sendDataMessage: UpdateNameMessage = {
          type: MessageTypes.UPDATE_NAME,
          fromId: user?.id ?? '',
          name: `${data.display_name} ${data.last_name}`,
        };

        for (const item of usersPsychologistList) {
          await agoraClient?.sendMessageToPeer({ text: JSON.stringify(sendDataMessage) }, item.id);
        }
      }

      dispatch(setUser(data));
      successfullyUpdatedProfile(true);
    }
  };

  return (
    <IonPage>
      <AppHeader className="flex flex-row items-start mb-4 cursor-pointer">
        <IconArrowLeft onClick={() => history.back()} />
      </AppHeader>
      <Container isFlexContainer>
        <div className={'flex flex-col gap-1'}>
          <Typography variant={TypographyVariantsEnum.Title28Semi}>{t('personal_detsils.edit_name.title')}</Typography>
        </div>
        <ScreenInput
          ref={inputRef}
          type={'text'}
          name={'client-name'}
          placeholder={t('personal_detsils.edit_name.title')}
          value={inputValue}
          onChange={(e: any) => setInputValue(e.target.value)}
          onKeyDown={(e: any) => e.key === 'Enter' && onSubmitAction()}
          required
        />
      </Container>
      <AppFooter transparent bottomSaveArea={!isKeyboardShown}>
        <Button
          isLoading={isLoading}
          disabled={!inputValue || isLoading}
          fullWidth
          size={'large'}
          onClick={() => onSubmitAction()}
        >
          {t('save_button')}
        </Button>
      </AppFooter>
    </IonPage>
  );
};

export default ProfileName;
